import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { SitecoreOddsPreferenceConfig } from '@frontend/sports/common/client-config-data-access';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { OddsAcceptance } from '@frontend/sports/types/betslip/request';
import { IUserBettingSettings, SyncUserSettingsService, UserSettingsService } from '@frontend/sports/user/feature';
import { DsButton } from '@frontend/ui/button';
import { DsListItem } from '@frontend/ui/list-item';
import { DsRadioModule } from '@frontend/ui/radio-button';
import { DsTooltipModule } from '@frontend/ui/tooltip';
import { ToastrQueueService, ToastrSchedule } from '@frontend/vanilla/core';
import { PlayerAttributesService } from '@frontend/vanilla/features/player-attributes';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Observable, map, tap } from 'rxjs';

import { OddChangeConfirmationService } from './odd-changes-confirmation.service';
import { OddsChangesConfirmationTrackingService } from './odds-changes-confirmation-tracking.service';

interface OddsOption {
    id: string;
    text: string;
    subtext?: string;
}

interface OddsPreference {
    acceptAnyOddsLabel: string;
    acceptAnyOddsSubtext: string;
    acceptOnlyHigherOddsLabel: string;
    acceptOnlyHigherOddsSubtext: string;
    description: string;
    dontAcceptOddChangesLabel: string;
    helperText: string;
    oddsPreferencePopupTitle: string;
    saveButton: string;
    tooltipText: string;
    oddChangePreferenceSuccess: string;
    oddChangePreferenceError: string;
}

@Component({
    selector: 'bs-odd-changes-confirmation',
    templateUrl: './odd-changes-confirmation.component.html',
    styleUrls: ['./odd-changes-confirmation.component.scss'],
    imports: [DsButton, DsTooltipModule, DsRadioModule, AsyncPipe, IconCustomComponent, DsListItem],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [OddsChangesConfirmationTrackingService],
})
export class OddChangesConfirmationComponent implements OnInit {
    oddSelection: string | undefined;
    oddsOptions: OddsOption[] = [];

    settings: IUserBettingSettings;
    oddsPreferenceConfig$: Observable<SitecoreOddsPreferenceConfig>;
    oddsPreferenceString: OddsPreference;

    constructor(
        private bettingSettingsService: SyncUserSettingsService,
        private userSettings: UserSettingsService,
        private activeModal: ActiveModal,
        public oddsPreferenceConfig: SitecoreOddsPreferenceConfig,
        public oddsChangeTrackingService: OddsChangesConfirmationTrackingService,
        private toaster: ToastrQueueService,
        private oddChangeConfirmationService: OddChangeConfirmationService,
        private playerAttributesService: PlayerAttributesService,
    ) {
        this.oddsPreferenceConfig$ = this.oddsPreferenceConfig.whenReady.pipe(
            map(() => this.oddsPreferenceConfig),
            tap((config: SitecoreOddsPreferenceConfig) => {
                this.oddsPreferenceString = config;
                this.oddsOptions = [
                    { id: OddsAcceptance.Any, text: config.acceptAnyOddsLabel, subtext: config.acceptAnyOddsSubtext },
                    { id: OddsAcceptance.Higher, text: config.acceptOnlyHigherOddsLabel, subtext: config.acceptOnlyHigherOddsSubtext },
                    { id: OddsAcceptance.No, text: config.dontAcceptOddChangesLabel, subtext: '' },
                ];
            }),
        );
    }

    ngOnInit() {
        this.settings = this.bettingSettingsService.bettingSettings;
        this.oddSelection = this.settings.oddsAcceptanceMode;
        this.oddsChangeTrackingService.trackPopupShown();
    }

    oddSelectionChange(oddsPreferenceValue: string) {
        this.oddSelection = oddsPreferenceValue;
    }

    saveOddPreference() {
        this.oddsChangeTrackingService.trackSaveButtonClick(this.oddSelection);
        this.userSettings
            .set({ oddsAcceptanceMode: this.oddSelection })
            .then(() => {
                this.toaster.add('OddChangePreferenceSuccess', { schedule: ToastrSchedule.Immediate });
                this.oddsChangeTrackingService.trackSavePreferenceStatus(
                    this.oddSelection,
                    this.oddsPreferenceString.oddChangePreferenceSuccess,
                    'success',
                );
                this.saveAcknowledgement();
            })
            .catch(() => {
                this.onSaveOddPreferenceFailure();
            });
        this.closePopup();
    }

    onSaveOddPreferenceFailure() {
        this.userSettings.set({ oddsAcceptanceMode: OddsAcceptance.No });

        this.toaster.add('OddChangePreferenceError', { schedule: ToastrSchedule.Immediate });

        this.oddsChangeTrackingService.trackSavePreferenceStatus(this.oddSelection, this.oddsPreferenceString.oddChangePreferenceError, 'error');
    }

    private closePopup(): void {
        this.activeModal.close();
    }

    closeTooltip() {
        this.oddsChangeTrackingService.trackTooltipClick(false);
    }

    openTooltip() {
        this.oddsChangeTrackingService.trackTooltipClick(true);
    }

    saveAcknowledgement() {
        this.oddChangeConfirmationService.updatePlayerPreference({ acknowledgement: { oddsChangePreference: true } }).subscribe({
            next: () => {
                this.playerAttributesService.refresh(false);
            },
        });
    }
}
